// A scan animates the row it changed: the bar fills, a completed line pulses green, and a scan that lands on a bar that
// is already full blinks the bar. Rows are the sheet rows ([data-scan-sheet] [data-scan-key]) and any element marked
// data-scan-blink, which blinks on every scan that raises its count. The server sends only the counts (data-scan-count)
// and which bars are full (data-bar-full); the animation comes from comparing the component's rows before and after the
// response to a scan. The Web Animations API is used because a Livewire morph resets the class attribute and would cut a
// class animation.

const scanRows = '[data-scan-sheet] [data-scan-key], [data-scan-blink]'
const fillMs = 300
const highlightMs = 900
const completeMs = 1300
const blinkMs = 700
const completeGreen = '#9AE6B4'
const blinkOnRed = '#7F1D1D'
const blinkOnLight = '#FFFFFF'
// A sheet fades in over 200 ms; a row scanned as the sheet opens has no layout until then.
const scrollFrames = 30

let watching = false
// Keyed by the Livewire message, so rows read for a message whose response never arrives cannot be used for another one.
const rowsBeforeScan = new WeakMap()
const running = new WeakMap()

// previous: the row before the response, or undefined for a new row.
export function scanRowAnimation(previous, row) {
  if (row.count <= (previous ? previous.count : 0)) return null
  if (!row.hasBar) return 'scrolled'
  if ((previous && previous.full) || row.over || row.extra || row.blinks) return 'blink'
  if (row.full) return 'complete'
  return 'fill'
}

// isScanMessage: true for a Livewire message that carried a scan (the scanner components tag them when sent).
export function watchScanRows(onLivewire, isScanMessage) {
  if (watching) return
  watching = true

  onLivewire('message.received', (message, component) => {
    if (isScanMessage(message) && component && component.el) rowsBeforeScan.set(message, readRows(component))
  })

  onLivewire('message.processed', (message, component) => {
    const previous = rowsBeforeScan.get(message)
    if (!previous || !component || !component.el) return
    rowsBeforeScan.delete(message)

    let scanned = null
    ownRows(component).forEach(el => {
      const row = readRow(el)
      const before = previous.get(row.key)
      const animation = scanRowAnimation(before, row)
      if (!animation) return
      scanned = el
      try {
        if (!reducedMotion()) animateRow(el, row, before, animation)
      } catch (error) {
        console.error(error)
      }
    })
    if (scanned) scrollWhenShown(scanned, scrollFrames)
  })
}

// Rows of a nested Livewire component belong to that component.
function ownRows(component) {
  return [...component.el.querySelectorAll(scanRows)].filter(el => !el.closest || el.closest('[wire\\:id]') === component.el)
}

function readRows(component) {
  const rows = new Map()
  ownRows(component).forEach(el => {
    const row = readRow(el)
    rows.set(row.key, row)
  })
  return rows
}

function readRow(el) {
  const bar = el.querySelector('.sc-bar')
  const fill = bar && bar.querySelector('.sc-bar-fill')
  return {
    key: el.dataset.scanKey,
    count: Number(el.dataset.scanCount) || 0,
    hasBar: !!bar,
    full: !!bar && bar.hasAttribute('data-bar-full'),
    over: el.hasAttribute('data-row-over'),
    extra: el.hasAttribute('data-row-extra'),
    blinks: el.hasAttribute('data-scan-blink'),
    width: fill ? fill.style.width : '0%',
    bar,
    fill,
    stub: bar && bar.querySelector('.sc-bar-stub'),
  }
}

function reducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function scrollWhenShown(el, frames) {
  if (el.getClientRects().length) return el.scrollIntoView({block: 'nearest'})
  if (frames > 0 && typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(() => scrollWhenShown(el, frames - 1))
}

function animateRow(el, row, before, animation) {
  if (animation === 'scrolled' || typeof el.animate !== 'function') return
  const current = running.get(el)
  // A blink still running carries on, so rapid scans never flash more than three times a second.
  if (animation === 'blink' && current && current.kind === 'blink' && current.animations.some(a => a.playState === 'running')) return
  if (current) current.animations.forEach(a => a.cancel())

  const animations = []
  const play = (target, keyframes, options) => target && animations.push(target.animate(keyframes, options))

  if (animation === 'blink') {
    play(row.bar, [
      {height: '4px'},
      {height: '8px', offset: 0.05},
      {height: '8px', offset: 0.86},
      {height: '4px'},
    ], {duration: blinkMs, easing: 'linear'})
    const flash = row.over ? blinkOnRed : blinkOnLight
    play(row.fill, blinkFrames(row.fill && getComputedStyle(row.fill).backgroundColor, flash), {duration: blinkMs})
    play(row.stub, blinkFrames(row.stub && getComputedStyle(row.stub).backgroundColor, flash), {duration: blinkMs})
  } else {
    play(row.fill, [{width: before ? before.width : '0%'}, {width: row.width}], {duration: fillMs, easing: 'ease-out'})
    play(row.fill, [{filter: 'brightness(1.5)'}, {filter: 'brightness(1)'}], {duration: highlightMs, easing: 'ease-out'})
  }

  if (animation === 'complete') {
    play(el, [
      {backgroundColor: completeGreen},
      {backgroundColor: completeGreen, offset: 0.23},
      {backgroundColor: getComputedStyle(el).backgroundColor},
    ], {duration: completeMs})
  }

  running.set(el, {kind: animation, animations})
}

// Two flashes in 700 ms.
function blinkFrames(colour, flash) {
  return [
    {backgroundColor: colour, offset: 0},
    {backgroundColor: colour, offset: 0.08},
    {backgroundColor: flash, offset: 0.12},
    {backgroundColor: flash, offset: 0.24},
    {backgroundColor: colour, offset: 0.36},
    {backgroundColor: colour, offset: 0.5},
    {backgroundColor: flash, offset: 0.62},
    {backgroundColor: flash, offset: 0.74},
    {backgroundColor: colour, offset: 0.86},
    {backgroundColor: colour, offset: 1},
  ]
}
